import { ApplicationInsights, ITelemetryItem, SeverityLevel } from '@microsoft/applicationinsights-web';

export { SeverityLevel };

export interface ApiFailureItem {
  url: string;
  status: number;
  statusText?: string;
  method?: string;
  route?: string;
  timestamp: string;
  errorMessage?: string;
  correlationId?: string;
}

let appInsights: ApplicationInsights | null = null;
let currentCorrelationId: string | null = null;
let handlersRegistered = false;
let breadcrumbs: any[] = [];
let recentApiFailures: ApiFailureItem[] = [];
let lastRoute: string | null = null;

const MAX_BREADCRUMBS = 20;
const MAX_API_FAILURES = 5;
const PROCESSED_BY_ERROR_BOUNDARY = '__processedByErrorBoundary__';

const SENSITIVE_KEYS = [
  'token', 'access_token', 'id_token', 'authorization', 'secret', 'password', 'bearer',
  'nif', 'iban', 'creditCard', 'cardNumber', 'cvv', 'ssn'
];

const REDACTED = '[REDACTED]';

const isDev = process.env.NODE_ENV !== 'production';

/**
 * Helper para gerar GUID (UUID v4)
 */
const createGUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Sanitiza URLs removendo query strings e identificadores nos caminhos
 */
const sanitizeUrl = (url?: string): string => {
  if (!url) return '';
  try {
    const cleanQuery = url.split('?')[0];
    return cleanQuery
      .replace(/\/[0-9]{6,}(\/|$)/g, '/[REDACTED]$1')
      .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '[REDACTED]');
  } catch (e) {
    return url;
  }
};

/**
 * Sanitiza dados sensíveis (PII) em objetos
 */
const sanitizeTelemetryData = (data: any): any => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(sanitizeTelemetryData);
  }

  const sanitized: Record<string, any> = {};
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const lowerKey = key.toLowerCase();
      const value = data[key];

      if (SENSITIVE_KEYS.some(sKey => lowerKey.includes(sKey.toLowerCase()))) {
        sanitized[key] = REDACTED;
      } else if (value && typeof value === 'object') {
        sanitized[key] = sanitizeTelemetryData(value);
      } else if (typeof value === 'string') {
        // Tenta detectar se a string contém algo que pareça um token ou NIF em texto plano se necessário, 
        // mas por agora mantemos a sanitização baseada em chaves conforme requisito.
        sanitized[key] = value;
      } else {
        sanitized[key] = value;
      }
    }
  }
  return sanitized;
};

/**
 * Helper para gerar Correlation ID
 */
export const generateCorrelationId = (): string => {
  return createGUID();
};

export const setCorrelationId = (id: string | null): void => {
  currentCorrelationId = id;
};

export const getCorrelationId = (): string | null => {
  return currentCorrelationId;
};

export const initAppInsights = (connectionString?: string): ApplicationInsights | null => {
  const connString = connectionString !== undefined ? connectionString : process.env.REACT_APP_APPLICATION_INSIGHTS_CONNECTION_STRING;

  if (!connString) {
    if (isDev) {
      console.warn('[AppInsights] Connection String não fornecida. Telemetria desactivada.');
    }
    return null;
  }

  const samplingPercentage = process.env.NODE_ENV === 'production' 
    ? (Number(process.env.REACT_APP_APPINSIGHTS_SAMPLING_PERCENTAGE) || 50)
    : 100;

  appInsights = new ApplicationInsights({
    config: {
      connectionString: connString,
      disableCookiesUsage: true,
      enableAutoRouteTracking: false,
      samplingPercentage: samplingPercentage,
    }
  });

  // Se o mock ou SDK retornar o objeto diretamente ou instanciado
  if (appInsights && typeof appInsights.loadAppInsights === 'function') {
    appInsights.loadAppInsights();
  }

  // Registrar Telemetry Initializer
  if (typeof appInsights.addTelemetryInitializer === 'function') {
    appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
    if (!envelope.data) envelope.data = {};
    
    // Propriedades Globais (customDimensions)
    // No SDK v3, as propriedades customizadas ficam em envelope.data
    const customDimensions = envelope.data;
    customDimensions.origin = 'portal';
    customDimensions.appVersion = '0.1.0';
    customDimensions.buildNumber = process.env.REACT_APP_BUILD_NBR || '';
    customDimensions.environment = process.env.REACT_APP_AZURE_APP_CONFIG_ENVIRONMENT_LABEL || process.env.NODE_ENV || 'development';
    customDimensions.userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    customDimensions.viewport = typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '';
    
    if (currentCorrelationId) {
      customDimensions.correlationId = currentCorrelationId;
    }

    // Sanitização de URL no envelope (baseData para PageViews)
    if (envelope.baseData) {
      if (envelope.baseData.url) envelope.baseData.url = sanitizeUrl(envelope.baseData.url);
      if (envelope.baseData.uri) envelope.baseData.uri = sanitizeUrl(envelope.baseData.uri);
    }

    // Sanitização profunda de todas as propriedades
    envelope.data = sanitizeTelemetryData(envelope.data);
    
    return true;
    });
  }

  return appInsights;
};

export const getAppInsights = (): ApplicationInsights | null => appInsights;

const runSafe = (helperName: string, action: (instance: ApplicationInsights) => void) => {
  if (!appInsights) {
    if (isDev) {
      console.warn(`[AppInsights] Helper ${helperName} chamado antes da inicialização.`);
    }
    return;
  }
  try {
    action(appInsights);
  } catch (e) {
    if (isDev) {
      console.error(`[AppInsights] Erro ao executar ${helperName}:`, e);
    }
  }
};

export const trackEvent = (name: string, properties?: Record<string, any>, measurements?: Record<string, number>): void => {
  runSafe('trackEvent', (instance) => {
    instance.trackEvent({ name, properties, measurements });
  });
};

export const trackException = (exception: any, severityLevel?: number, properties?: Record<string, any>): void => {
  let errorObj: Error;
  if (exception instanceof Error) {
    errorObj = exception;
  } else if (exception && typeof exception === 'object') {
    errorObj = new Error(exception.message || JSON.stringify(exception));
  } else if (typeof exception === 'string') {
    errorObj = new Error(exception);
  } else {
    errorObj = new Error('Unknown error');
  }

  runSafe('trackException', (instance) => {
    instance.trackException({ exception: errorObj, severityLevel, properties });
  });
};

export const startTrackEvent = (name: string): void => {
  runSafe('startTrackEvent', (instance) => {
    instance.startTrackEvent(name);
  });
};

export const stopTrackEvent = (name: string, properties?: Record<string, any>, measurements?: Record<string, number>): void => {
  runSafe('stopTrackEvent', (instance) => {
    instance.stopTrackEvent(name, properties, measurements);
  });
};

export const trackPageView = (name?: string, url?: string, properties?: Record<string, any>): void => {
  runSafe('trackPageView', (instance) => {
    instance.trackPageView({ 
      name, 
      uri: url ? sanitizeUrl(url) : undefined,
      properties
    });
  });
};

/**
 * Registra handlers globais para erros não tratados e promessas rejeitadas.
 * Idempotente via handlersRegistered.
 */
export const setupGlobalErrorHandlers = (): void => {
  if (handlersRegistered || typeof window === 'undefined') return;

  const originalOnError = window.onerror;
  window.onerror = (message, filename, lineno, colno, error) => {
    // Evitar duplicados se o erro já foi processado pelo ErrorBoundary
    if (error && (error as any)[PROCESSED_BY_ERROR_BOUNDARY]) {
      if (originalOnError) return originalOnError(message, filename, lineno, colno, error);
      return;
    }

    const properties = {
      errorType: 'unhandled_js_error',
      isFatal: true,
      filename,
      lineno,
      colno,
      lastRoute: getLastRoute() || (typeof window !== 'undefined' && window.location ? window.location.pathname : ''),
      breadcrumbs: getBreadcrumbs(),
    };

    const errorToTrack = error instanceof Error ? error : new Error(String(message || 'Unknown error'));
    trackException(errorToTrack, SeverityLevel.Error, properties);

    if (originalOnError) {
      return originalOnError(message, filename, lineno, colno, error);
    }
  };

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    
    // Evitar duplicados
    if (reason && (reason as any)[PROCESSED_BY_ERROR_BOUNDARY]) {
      return;
    }

    const properties = {
      errorType: 'unhandled_promise_rejection',
      isFatal: true,
      lastRoute: getLastRoute() || (typeof window !== 'undefined' && window.location ? window.location.pathname : ''),
      breadcrumbs: getBreadcrumbs(),
    };

    const errorToTrack = reason instanceof Error ? reason : new Error(String(reason || 'Unhandled Promise Rejection'));
    trackException(errorToTrack, SeverityLevel.Error, properties);
  });

  handlersRegistered = true;
};

/**
 * Garante o envio de telemetria pendente.
 */
export const flushAppInsights = (): void => {
  runSafe('flush', (instance) => {
    if (typeof instance.flush === 'function') {
      instance.flush();
    }
  });
};

/**
 * Configura listeners para descarregar a telemetria ao sair da página.
 */
export const setupTelemetryFlushListeners = (): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  window.addEventListener('beforeunload', () => flushAppInsights());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushAppInsights();
    }
  });
};

/**
 * Marca um erro como processado para evitar reporte duplicado pelos handlers globais.
 */
export const markErrorAsProcessed = (error: any): any => {
  if (error && typeof error === 'object') {
    try {
      (error as any)[PROCESSED_BY_ERROR_BOUNDARY] = true;
    } catch (e) {
      // Ignora se o objeto não for extensível
    }
  }
  return error;
};

/**
 * Adiciona um breadcrumb ao buffer circular.
 */
export const addBreadcrumb = (breadcrumb: string | { flow?: string, step?: string, message?: string }): void => {
  const item = typeof breadcrumb === 'string' 
    ? { message: breadcrumb, timestamp: new Date().toISOString() } 
    : { ...breadcrumb, timestamp: new Date().toISOString() };
    
  breadcrumbs.push(item);
  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.shift();
  }
};

/**
 * Retorna cópia dos breadcrumbs atuais.
 */
export const getBreadcrumbs = (): any[] => [...breadcrumbs];

/**
 * Alias para getBreadcrumbs.
 */
export const getLastBreadcrumbs = (): any[] => getBreadcrumbs();

/**
 * Limpa o buffer de breadcrumbs.
 */
export const clearBreadcrumbs = (): void => {
  breadcrumbs = [];
};

/**
 * Regista uma falha de pedido de API no buffer circular de diagnóstico.
 */
export const recordApiFailure = (failure: Omit<ApiFailureItem, 'timestamp'> & { timestamp?: string }): void => {
  const item: ApiFailureItem = {
    ...failure,
    url: sanitizeUrl(failure.url),
    timestamp: failure.timestamp || new Date().toISOString(),
    route: failure.route || lastRoute || (typeof window !== 'undefined' && window.location ? window.location.pathname : ''),
    correlationId: failure.correlationId || currentCorrelationId || undefined,
  };

  recentApiFailures.push(item);
  if (recentApiFailures.length > MAX_API_FAILURES) {
    recentApiFailures.shift();
  }

  // Registar breadcrumb de falha de API
  addBreadcrumb({
    flow: 'api_failure',
    message: `${item.method || 'API'} ${item.url} [status: ${item.status}]`,
  });
};

/**
 * Retorna cópia das falhas de API recentes.
 */
export const getRecentApiFailures = (): ApiFailureItem[] => [...recentApiFailures];

/**
 * Retorna a última falha de API registada.
 */
export const getLastFailedApi = (): ApiFailureItem | null => {
  return recentApiFailures.length > 0 ? recentApiFailures[recentApiFailures.length - 1] : null;
};

/**
 * Limpa o buffer de falhas de API.
 */
export const clearApiFailures = (): void => {
  recentApiFailures = [];
};

/**
 * Define a última rota visitada.
 */
export const setLastRoute = (route: string): void => {
  lastRoute = route;
};

/**
 * Retorna a última rota visitada.
 */
export const getLastRoute = (): string | null => lastRoute;

export type UIInteractionType = 'button' | 'menu' | 'modal' | 'tab' | 'action';

export interface UIInteractionProps {
  actionId?: string;
  type: UIInteractionType;
  action: 'click' | 'open' | 'close' | 'submit' | 'cancel' | 'select' | string;
  label: string;
  section?: string;
  category?: string;
  route?: string;
  buId?: string;
  properties?: Record<string, any>;
  measurements?: Record<string, number>;
}

/**
 * Normaliza string para uso em identificadores de telemetria (slug)
 */
export const slugify = (text: string): string => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove diacríticos / acentos
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

/**
 * Rastreia uma interação do utilizador na interface (botão, modal, menu, etc.)
 */
export const trackUserInteraction = (interaction: UIInteractionProps): void => {
  if (!interaction) return;

  const currentRoute = interaction.route || lastRoute || (typeof window !== 'undefined' && window.location ? window.location.pathname : '') || '';
  const cleanRoute = currentRoute.split('?')[0];

  let actionId = interaction.actionId;
  if (!actionId) {
    const routeSlug = cleanRoute.replace(/^\/+|\/+$/g, '').replace(/[-/_]+/g, '_') || 'root';
    const sectionSlug = interaction.section ? slugify(interaction.section) : '';
    const labelSlug = slugify(interaction.label);
    if (sectionSlug) {
      actionId = `${routeSlug}.${sectionSlug}.${interaction.type}_${labelSlug || 'element'}`;
    } else {
      actionId = `${routeSlug}.${interaction.type}_${labelSlug || 'element'}`;
    }
  }

  // Registar breadcrumb de interação na trilha de diagnóstico
  addBreadcrumb({
    flow: 'ui_interaction',
    step: interaction.type,
    message: `${interaction.type} [${interaction.action}]: ${interaction.label || actionId}`,
  });

  const payload: Record<string, any> = {
    actionId,
    interactionType: interaction.type,
    action: interaction.action,
    elementLabel: interaction.label,
    currentRoute: cleanRoute,
    ...(interaction.section ? { section: interaction.section } : {}),
    ...(interaction.category ? { category: interaction.category } : {}),
    ...(interaction.buId ? { buId: interaction.buId } : {}),
    ...(interaction.properties || {}),
  };

  trackEvent('UIInteraction', payload, interaction.measurements);
};

/**
 * Helper de conveniência para rastrear clique de botão
 */
export const trackButtonClick = (
  label: string,
  category?: string,
  properties?: Record<string, any>,
  actionId?: string,
  section?: string
): void => {
  trackUserInteraction({
    type: 'button',
    action: 'click',
    label,
    category,
    properties,
    actionId,
    section,
  });
};

/**
 * Helper de conveniência para rastrear seleção de menu
 */
export const trackMenuClick = (
  label: string,
  targetUrl?: string,
  properties?: Record<string, any>,
  actionId?: string
): void => {
  trackUserInteraction({
    type: 'menu',
    action: 'select',
    label,
    properties: {
      ...(targetUrl ? { targetUrl: sanitizeUrl(targetUrl) } : {}),
      ...(properties || {}),
    },
    actionId,
  });
};

/**
 * Helper de conveniência para rastrear ações em modais
 */
export const trackModalAction = (
  modalId: string,
  action: 'open' | 'close' | 'submit' | 'cancel' | string,
  properties?: Record<string, any>
): void => {
  const normalizedId = slugify(modalId) || 'modal';
  trackUserInteraction({
    type: 'modal',
    action,
    label: modalId,
    actionId: normalizedId.startsWith('modal_') ? normalizedId : `modal_${normalizedId}`,
    properties,
  });
};

/**
 * Helper de conveniência para rastrear abertura do Chatbot
 */
export const trackChatbotOpen = (
  source: string = 'talk_with_us',
  properties?: Record<string, any>
): void => {
  trackUserInteraction({
    actionId: source === 'floating_widget' ? 'floating_chatbot.button_open' : 'chatbot.button_open',
    type: 'button',
    action: 'open',
    label: 'Assistente Virtual',
    section: 'chatbot',
    properties: {
      source,
      ...(properties || {}),
    },
  });
};

/**
 * Registra listener global para capturar cliques no widget flutuante do Chatbot (Visor).
 */
export const setupChatbotTracking = (): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  document.addEventListener(
    'click',
    (event: MouseEvent) => {
      try {
        // Se o evento foi disparado programaticamente (ex: chatbotElement.click() de TalkWithUs), não duplicar
        if ((window as any).__openingChatbotProgrammatically) return;

        const target = event.target as HTMLElement | null;
        if (!target) return;

        const isFloatingChatbot =
          target.closest?.('.floating-logo') ||
          target.closest?.('#example-widget-container') ||
          target.closest?.('[id*="visor"]') ||
          target.closest?.('[class*="floating-logo"]');

        if (isFloatingChatbot) {
          const currentRoute = window.location ? window.location.pathname : '';
          trackUserInteraction({
            actionId: 'floating_chatbot.button_open',
            type: 'button',
            action: 'open',
            label: 'Chatbot Floating Widget',
            section: 'chatbot',
            route: currentRoute,
            properties: {
              source: 'floating_widget',
            },
          });
        }
      } catch (e) {
        // Ignora erros na verificação do elemento
      }
    },
    true
  );
};
